import React, { useEffect, useState } from "react";

// Compacte deadline-editor voor de detail-page header. Toont een readable
// "over 5 dagen" / "3 dagen verlopen" pill in display-mode, en een datum-
// input + note-veld in edit-mode. Server stuurt reminder-mails op 7d/4d/0d/
// elke 7 dagen overdue.

const toIsoDate = (d) => {
  if (!d) return "";
  try {
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    // YYYY-MM-DD voor <input type="date">. We willen geen UTC-shift dus
    // gebruiken we de lokale datum-componenten.
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
};

const daysUntil = (dateLike) => {
  if (!dateLike) return null;
  const target = new Date(dateLike);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  // Verschil in dagen op basis van UTC-midden om DST-randgevallen te
  // vermijden. We rekenen alleen op datum-precisie, niet uur.
  const targetStart = Date.UTC(
    target.getFullYear(),
    target.getMonth(),
    target.getDate(),
  );
  const todayStart = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  return Math.round((targetStart - todayStart) / (1000 * 60 * 60 * 24));
};

const formatRelative = (days) => {
  if (days == null) return null;
  if (days === 0) return "vandaag";
  if (days === 1) return "morgen";
  if (days === -1) return "gisteren verlopen";
  if (days > 0) return `over ${days} dagen`;
  return `${-days} dagen verlopen`;
};

const toneFor = (days) => {
  if (days == null) return "bg-gray-100 text-gray-700";
  if (days < 0) return "bg-rose-100 text-rose-800";
  if (days <= 3) return "bg-amber-100 text-amber-800";
  if (days <= 7) return "bg-sky-100 text-sky-800";
  return "bg-emerald-100 text-emerald-800";
};

const REMINDER_OPTIONS = [
  { value: "7d", label: "7 dagen ervoor" },
  { value: "4d", label: "4 dagen ervoor" },
  { value: "0d", label: "Op de dag zelf" },
  { value: "overdue", label: "Wekelijks bij overdue" },
];
const DEFAULT_SCHEDULE = ["7d", "4d", "0d", "overdue"];

const BtwDeadlinePicker = ({
  value,
  note,
  schedule,
  onSave,
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [dateInput, setDateInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [scheduleInput, setScheduleInput] = useState(DEFAULT_SCHEDULE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!editing) {
      setDateInput(toIsoDate(value));
      setNoteInput(note ?? "");
      setScheduleInput(
        Array.isArray(schedule) && schedule.length > 0
          ? schedule
          : DEFAULT_SCHEDULE,
      );
      setError(null);
    }
  }, [editing, value, note, schedule]);

  const toggleReminder = (key) => {
    setScheduleInput((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    );
  };

  const days = daysUntil(value);
  const relative = formatRelative(days);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Lege datum = clearen. Anders ISO-datum (00:00 lokaal -> server slaat
      // 'm op als UTC en de cron rekent met NL-tijd).
      const payload = dateInput
        ? {
            deadlineAt: new Date(`${dateInput}T08:00:00`).toISOString(),
            deadlineNote: noteInput.trim(),
            reminderSchedule: scheduleInput,
          }
        : { deadlineAt: null, deadlineNote: "", reminderSchedule: [] };
      await onSave?.(payload);
      setEditing(false);
    } catch (e) {
      setError(
        e?.response?.data?.error ?? e?.message ?? "Opslaan mislukt.",
      );
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave?.({ deadlineAt: null, deadlineNote: "" });
      setEditing(false);
    } catch (e) {
      setError(e?.response?.data?.error ?? "Wissen mislukt.");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        <input
          type="date"
          value={dateInput}
          onChange={(e) => setDateInput(e.target.value)}
          disabled={saving || disabled}
          className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
        <input
          type="text"
          value={noteInput}
          onChange={(e) => setNoteInput(e.target.value)}
          disabled={saving || disabled}
          maxLength={500}
          placeholder="Reden (bv. uiterste reactie-datum klant)"
          className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
        {dateInput ? (
          <div className="rounded-md bg-gray-50 p-2 ring-1 ring-gray-200">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Stuur mij een reminder
            </p>
            <div className="flex flex-col gap-1">
              {REMINDER_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 text-xs text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={scheduleInput.includes(opt.value)}
                    onChange={() => toggleReminder(opt.value)}
                    disabled={saving || disabled}
                    className="h-3.5 w-3.5 rounded border-gray-300"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        ) : null}
        {error ? (
          <p className="text-[11px] text-rose-700">{error}</p>
        ) : null}
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={save}
            disabled={saving || disabled}
            className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          {value ? (
            <button
              type="button"
              onClick={clear}
              disabled={saving || disabled}
              className="ml-auto rounded-md px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            >
              Wissen
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-2.5 py-1 text-sm hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
    >
      {value ? (
        <>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${toneFor(days)}`}
          >
            {relative}
          </span>
          <span className="text-gray-700">
            {new Date(value).toLocaleDateString("nl-NL")}
          </span>
        </>
      ) : (
        <span className="text-gray-400">Geen deadline · klik om in te stellen</span>
      )}
    </button>
  );
};

export default BtwDeadlinePicker;
